/**
 * Boot-health signal store — a tiny observable flag flipped when IndexedDB
 * reports the offline database is BLOCKED.
 *
 * IndexedDB fires `blocked` when this tab tries to upgrade (a deploy bumped
 * `DB_VERSION`) or delete the database while ANOTHER connection — almost always
 * a second tab of this app — holds it open at the old version. When that
 * happens, `offlineDb.open()` never resolves: the SyncEngine can't write a
 * single `_syncMeta` row, the critical wave never becomes ready, and the global
 * boot splash hangs forever with nothing downloaded and no way out (the exact
 * "close the other tab / clear your cache" failure users hit).
 *
 * `db.ts` calls `notifyDbBlocked()` from its Dexie `blocked` handler; the boot
 * screen subscribes so it can (a) trip its recovery UI immediately instead of
 * waiting out the full stall timeout, and (b) show a tailored "another tab is
 * blocking startup" message.
 *
 * Module-level singleton, mirroring `db-epoch.ts`: there is exactly one offline
 * DB and one SyncEngine (see apps/web/CLAUDE.md), so a single shared flag is the
 * whole surface. The primitive `get*` snapshots are safe for
 * `useSyncExternalStore`.
 */

let dbBlocked = false
const listeners = new Set<() => void>()

/** True once IndexedDB has reported the offline DB blocked this session. */
export function getDbBlocked(): boolean {
  return dbBlocked
}

/**
 * Flip the blocked flag and notify subscribers. Idempotent — repeated
 * `blocked` events (IndexedDB can fire several) notify only on the first
 * transition, so subscribers don't churn.
 */
export function notifyDbBlocked(): void {
  if (dbBlocked) return
  dbBlocked = true
  for (const listener of listeners) listener()
}

/**
 * Clear the blocked flag and notify subscribers. Called when the offline DB
 * opens successfully (Dexie `ready`): the connection that was blocking us has
 * released, so the diagnosis no longer holds and a later boot attempt must not
 * inherit a stale `blocked` verdict (and its shortened stall fuse). Idempotent.
 */
export function clearDbBlocked(): void {
  if (!dbBlocked) return
  dbBlocked = false
  for (const listener of listeners) listener()
}

/** Subscribe to blocked-state changes. Returns an unsubscribe handle. */
export function subscribeDbBlocked(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test helper — reset the flag + drop listeners between runs. */
export function __resetBootHealthForTests(): void {
  dbBlocked = false
  listeners.clear()
}
